import { supabase } from '@/integrations/supabase/client';
import { gtmException } from '@/lib/gtmDataLayer';

/**
 * Hard-cutover gate: every customer-facing checkout exit MUST go through the
 * server-side `checkout-proxy` edge function. The proxy validates host
 * (checkout.zooi.se / zooi-se.myshopify.com), HMAC-signs the URL with a 5-min
 * TTL, logs to checkout_proxy_log and 302-redirects to Shopify.
 *
 * 2026-05-12 hard cutover:
 *   PRIMARY (only customer-facing URL):
 *     https://go.zooi.se/checkout/<token>
 *
 *   FORBIDDEN as primary:
 *     https://zooi.se/go/checkout/<token>   (legacy apex worker — diagnostic only)
 *     https://*.supabase.co/functions/v1/checkout-proxy?u=…  (legacy edge URL)
 *
 *   If the canonical URL is missing or malformed we fall back DIRECTLY to the
 *   raw Shopify checkout URL — never to a legacy proxy URL — so customers can
 *   still pay while the canonical worker is recovered.
 */
export type CheckoutProxyResponse = {
  proxyUrl?: string | null;
  legacyProxyUrl?: string | null;
};

const CANONICAL_HOST = 'go.zooi.se';
const CANONICAL_PATH_PREFIX = '/checkout/';

/** True only for the canonical https://go.zooi.se/checkout/<token> URL. */
export function isCanonicalCheckoutUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    return (
      u.protocol === 'https:' &&
      u.host === CANONICAL_HOST &&
      u.pathname.startsWith(CANONICAL_PATH_PREFIX) &&
      u.pathname.length > CANONICAL_PATH_PREFIX.length
    );
  } catch {
    return false;
  }
}

export async function wrapWithCheckoutProxy(
  shopifyCheckoutUrl: string,
  cartId: string | null,
): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('checkout-proxy', {
      body: { checkoutUrl: shopifyCheckoutUrl, cartId },
    });
    if (error) {
      gtmException('checkout_proxy_sign_failed', false);
      return shopifyCheckoutUrl;
    }
    const d = data as CheckoutProxyResponse | null;
    const canonical = d?.proxyUrl ?? null;

    // Hard rule: ONLY the canonical https://go.zooi.se/checkout/<token>
    // URL is acceptable as a customer-facing checkout URL. Anything else
    // (including legacyProxyUrl) is rejected and we fall back to the raw
    // Shopify URL so the customer is never blocked.
    if (canonical && isCanonicalCheckoutUrl(canonical)) {
      return canonical;
    }

    if (canonical) {
      // Edge function returned a non-canonical proxy URL — log and skip.
      gtmException('checkout_proxy_non_canonical', false);
    } else {
      gtmException('checkout_proxy_no_url', false);
    }
    return shopifyCheckoutUrl;
  } catch (e: unknown) {
    void e;
    gtmException('checkout_proxy_exception', false);
    return shopifyCheckoutUrl;
  }
}

/**
 * Validates that a URL is a recognised checkout proxy URL of any era.
 *
 * Used by tests and diagnostic tools (e.g. probe pages) — NOT used to gate
 * customer redirects. The customer-facing primary path is enforced by
 * `isCanonicalCheckoutUrl` above.
 */
export function isValidCheckoutProxyUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const isCanonical = u.host === CANONICAL_HOST && u.pathname.startsWith(CANONICAL_PATH_PREFIX);
    const isLegacyApex = u.host === 'zooi.se' && u.pathname.startsWith('/go/checkout/');
    const isLegacyEdge =
      u.pathname.endsWith('/functions/v1/checkout-proxy') &&
      u.searchParams.has('u') &&
      u.searchParams.has('ts') &&
      u.searchParams.has('sig');
    return isCanonical || isLegacyApex || isLegacyEdge;
  } catch {
    return false;
  }
}
